import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = '/api';

function formatBytes(bytes) {
  if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(1)} TB`;
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${(bytes / 1e3).toFixed(1)} KB`;
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState(null);

  const headers = () => {
    const h = { 'Content-Type': 'application/json' };
    const t = localStorage.getItem('logos_session_token');
    if (t) h['Authorization'] = `Bearer ${t}`;
    return h;
  };

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin') { navigate('/'); return; }
    loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, checkpointsRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers: headers(), credentials: 'include' }),
        fetch(`${API}/admin/users`, { headers: headers(), credentials: 'include' }),
        fetch(`${API}/admin/checkpoints`, { headers: headers(), credentials: 'include' })
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (checkpointsRes.ok) setCheckpoints(await checkpointsRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateUser = async (username, updates) => {
    setActionMsg(null);
    const res = await fetch(`${API}/admin/verify`, {
      method: 'POST', headers: headers(), credentials: 'include',
      body: JSON.stringify({ username, ...updates })
    });
    if (res.ok) {
      setActionMsg(`updated ${username}`);
      loadData();
    } else {
      const err = await res.json().catch(() => ({}));
      setActionMsg(err.detail || 'failed');
    }
    setTimeout(() => setActionMsg(null), 3000);
  };

  const deleteCheckpoint = async (checkpointId, name) => {
    if (!window.confirm(`delete "${name}"? this cannot be undone.`)) return;
    const res = await fetch(`${API}/checkpoints/${checkpointId}`, {
      method: 'DELETE', headers: headers(), credentials: 'include'
    });
    if (res.ok) {
      setActionMsg(`deleted ${name}`);
      loadData();
    }
    setTimeout(() => setActionMsg(null), 3000);
  };

  if (!user || user.role !== 'admin') return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <p className="text-[#888888] lowercase">admin access required</p>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <p className="text-[#888888] lowercase animate-pulse">loading admin dashboard...</p>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'overview' },
    { id: 'users', label: `users (${users.length})` },
    { id: 'checkpoints', label: `checkpoints (${checkpoints.length})` }
  ];

  return (
    <div className="min-h-screen bg-black py-12 px-6 lg:px-8 page-wrapper" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase mb-8">admin</h1>

        {/* Action message */}
        {actionMsg && (
          <div className="mb-4 p-3 border border-[#383f38] rounded-lg text-white lowercase text-sm" data-testid="action-msg">
            {actionMsg}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-[#1e251f]">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 lowercase text-sm transition-all duration-300 border-b-2 -mb-px ${
                tab === t.id ? 'text-white border-white' : 'text-[#555555] border-transparent hover:text-[#888888]'
              }`}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === 'overview' && stats && (
          <div className="space-y-8" data-testid="overview-tab">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="total users" value={stats.total_users} />
              <StatCard label="total checkpoints" value={stats.total_checkpoints} />
              <StatCard label="total downloads" value={stats.total_downloads.toLocaleString()} />
              <StatCard label="storage used" value={formatBytes(stats.total_storage_bytes)} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="soma downloads" value={stats.soma_downloads?.toLocaleString() || '0'} />
              <StatCard label="admins" value={stats.admin_count} />
              <StatCard label="verified users" value={stats.verified_count} />
              <StatCard label="checkpoints this week" value={stats.checkpoints_this_week} />
            </div>
          </div>
        )}

        {/* Users */}
        {tab === 'users' && (
          <div className="space-y-2" data-testid="users-tab">
            {/* Desktop header */}
            <div className="hidden lg:grid grid-cols-[1fr_1fr_80px_80px_80px_120px] gap-4 px-4 py-2 text-[#555555] text-xs lowercase font-mono border-b border-[#1e251f]">
              <span>username</span><span>email</span><span>role</span><span>verified</span><span>checkpoints</span><span>actions</span>
            </div>
            {users.map(u => (
              <div key={u.user_id} className="border-b border-[#151b17] hover:bg-[#0e120f] transition-colors" data-testid={`user-row-${u.username}`}>
                {/* Desktop row */}
                <div className="hidden lg:grid grid-cols-[1fr_1fr_80px_80px_80px_120px] gap-4 px-4 py-3 items-center">
                  <span className="text-white lowercase text-sm truncate">{u.username}</span>
                  <span className="text-[#888888] text-sm truncate">{u.email}</span>
                  <span className={`text-xs lowercase ${u.role === 'admin' ? 'text-white' : 'text-[#555555]'}`}>{u.role || 'user'}</span>
                  <span className={`text-xs lowercase ${u.verified ? 'text-white' : 'text-[#555555]'}`}>{u.verified ? 'yes' : 'no'}</span>
                  <span className="text-[#888888] text-sm font-mono">{u.checkpoint_count}</span>
                  <div className="flex gap-2">
                    <button onClick={() => updateUser(u.username, { verified: !u.verified })} className="text-[#555555] hover:text-white text-xs lowercase transition-colors" data-testid={`verify-${u.username}`}>
                      {u.verified ? 'unverify' : 'verify'}
                    </button>
                    <button onClick={() => updateUser(u.username, { role: u.role === 'admin' ? 'user' : 'admin' })} className="text-[#555555] hover:text-white text-xs lowercase transition-colors" data-testid={`role-${u.username}`}>
                      {u.role === 'admin' ? 'demote' : 'promote'}
                    </button>
                  </div>
                </div>
                {/* Mobile card */}
                <div className="lg:hidden px-4 py-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-white lowercase text-sm font-bold">{u.username}</span>
                    <span className={`text-xs lowercase px-2 py-0.5 border border-[#383f38] rounded ${u.role === 'admin' ? 'text-white' : 'text-[#555555]'}`}>{u.role || 'user'}</span>
                  </div>
                  <p className="text-[#888888] text-xs truncate">{u.email}</p>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-[#555555]">{u.checkpoint_count} checkpoints</span>
                    <span className={u.verified ? 'text-white' : 'text-[#555555]'}>{u.verified ? 'verified' : 'unverified'}</span>
                  </div>
                  <div className="flex gap-3 pt-1">
                    <button onClick={() => updateUser(u.username, { verified: !u.verified })} className="text-[#555555] hover:text-white text-xs lowercase transition-colors">
                      {u.verified ? 'unverify' : 'verify'}
                    </button>
                    <button onClick={() => updateUser(u.username, { role: u.role === 'admin' ? 'user' : 'admin' })} className="text-[#555555] hover:text-white text-xs lowercase transition-colors">
                      {u.role === 'admin' ? 'demote' : 'promote'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Checkpoints */}
        {tab === 'checkpoints' && (
          <div className="space-y-2" data-testid="checkpoints-tab">
            {/* Desktop header */}
            <div className="hidden lg:grid grid-cols-[1fr_120px_100px_100px_80px] gap-4 px-4 py-2 text-[#555555] text-xs lowercase font-mono border-b border-[#1e251f]">
              <span>name</span><span>creator</span><span>downloads</span><span>size</span><span>actions</span>
            </div>
            {checkpoints.map(c => (
              <div key={c.checkpoint_id} className="border-b border-[#151b17] hover:bg-[#0e120f] transition-colors" data-testid={`ckpt-row-${c.checkpoint_id.slice(0,8)}`}>
                {/* Desktop */}
                <div className="hidden lg:grid grid-cols-[1fr_120px_100px_100px_80px] gap-4 px-4 py-3 items-center">
                  <div>
                    <a href={`/checkpoints/${c.checkpoint_id}`} className="text-white lowercase text-sm hover:underline">{c.name}</a>
                    <p className="text-[#444444] text-xs truncate">{c.checkpoint_id.slice(0, 16)}...</p>
                  </div>
                  <span className="text-[#888888] text-sm truncate lowercase">{c.creator_username}</span>
                  <span className="text-[#888888] text-sm font-mono">{c.download_count}</span>
                  <span className="text-[#888888] text-sm font-mono">{formatBytes(c.file_size_bytes || 0)}</span>
                  <button onClick={() => deleteCheckpoint(c.checkpoint_id, c.name)} className="text-[#555555] hover:text-white text-xs lowercase transition-colors">delete</button>
                </div>
                {/* Mobile */}
                <div className="lg:hidden px-4 py-4 space-y-2">
                  <a href={`/checkpoints/${c.checkpoint_id}`} className="text-white lowercase text-sm font-bold hover:underline block">{c.name}</a>
                  <div className="flex items-center gap-4 text-xs text-[#888888]">
                    <span className="lowercase">{c.creator_username}</span>
                    <span className="font-mono">{c.download_count} dl</span>
                    <span className="font-mono">{formatBytes(c.file_size_bytes || 0)}</span>
                  </div>
                  <button onClick={() => deleteCheckpoint(c.checkpoint_id, c.name)} className="text-[#555555] hover:text-white text-xs lowercase transition-colors">delete</button>
                </div>
              </div>
            ))}
            {checkpoints.length === 0 && (
              <p className="text-[#555555] lowercase text-sm px-4 py-8">no checkpoints yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="border border-[#272e28] rounded-xl p-5 gradient-card">
      <p className="text-[#666666] lowercase text-xs mb-1 font-mono">{label}</p>
      <p className="text-white text-2xl font-bold">{value}</p>
    </div>
  );
}
